import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import AddPersonDialog from "@/components/AddPersonDialog";
import RowActions from "@/components/RowActions";
import ConfirmDelete from "@/components/ConfirmDelete";
import BrainTypeField from "@/components/BrainTypeField";
import ClientTypeField, { CLIENT_TYPE_LABEL } from "@/components/ClientTypeField";
import ClientRequestBadges from "@/components/ClientRequestBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { rowColorForUser } from "@/lib/rowColor";
import {
  ArrowLeft,
  ChevronRight,
  UserCircle2,
  Mail,
  Users,
  CheckCircle2,
  Circle,
  Plus,
  BellRing,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "", brain_type: null, client_type: "prospect" };

export default function CoachDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  // Edit dialog state
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete state
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canManage = user?.role === "super_admin" || user?.role === "institute_admin";

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/coaches/${id}`),
        api.get(`/coaches/${id}/users`),
      ]);
      setCoach(a.data);
      setUsers(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      brain_type: u.brain_type ?? null,
      client_type: u.client_type || "prospect",
    });
    setEditError("");
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditError("");
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.password) payload.password = form.password;
      if (form.brain_type != null) payload.brain_type = form.brain_type;
      if (form.client_type) payload.client_type = form.client_type;
      await api.put(`/users/${editing.id}`, payload);
      toast.success("Client updated");
      setEditOpen(false);
      load();
    } catch (err) {
      setEditError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${confirmTarget.id}`);
      toast.success("Client deleted");
      setConfirmTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  // Mark every pending coaching request for a user as resolved (hides badges)
  const markHandled = async (e, userId) => {
    e.stopPropagation();
    try {
      await api.post(`/users/${userId}/coaching-requests/resolve-all`);
      toast.success("Request marked handled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Aggregate counts for the top banner (mirrors CoachUsers.jsx)
  const clientsWantingBusiness = users.filter(
    (u) => u.pending_requests && u.pending_requests.length > 0
  );
  const totalRequests = clientsWantingBusiness.reduce(
    (n, u) => n + u.pending_requests.length,
    0
  );

  const backHref = useMemo(() => {
    if (!user) return "/";
    if (user.role === "super_admin" && coach?.institute_id) {
      return `/details/institute/${coach.institute_id}`;
    }
    if (user.role === "institute_admin") return "/institute/coaches";
    return "/";
  }, [user, coach]);

  const backLabel = useMemo(() => {
    if (!user) return "Back";
    if (user.role === "super_admin") return "Institute";
    return "All Coaches";
  }, [user]);

  if (loading || !coach) {
    return (
      <div className="px-8 lg:px-12 py-12">
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold">Loading…</p>
      </div>
    );
  }

  return (
    <div className="px-8 lg:px-12 py-12 max-w-7xl" data-testid="coach-detail-page">
      <Link
        to={backHref}
        data-testid="coach-detail-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8A9188] hover:text-[#1A1D1A] font-bold mb-6"
      >
        <ArrowLeft strokeWidth={1.5} size={14} />
        {backLabel}
      </Link>

      <PageHeader overline="Coach" title={coach.name} description={coach.email} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <Stat icon={UserCircle2} label="Name" value={coach.name} />
        <Stat icon={Mail} label="Email" value={coach.email} />
        <Stat icon={Users} label="Clients" value={users.length} hint={coach.brain_type ? `Brain type · ${coach.brain_type}` : "Active"} />
      </div>

      <h2 className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold mb-4 flex items-center justify-between">
        <span>Clients of this coach</span>
        {canManage && (
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="coach-detail-add-user-button"
            className="h-10 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white text-sm font-medium"
          >
            <Plus strokeWidth={1.5} size={16} className="mr-1.5" />
            Add Client
          </Button>
        )}
      </h2>

      {clientsWantingBusiness.length > 0 && (
        <div
          className="mb-6 bg-[#F5EAE2] border border-[#E5D2C2] rounded-2xl p-5 sm:p-6 flex items-start gap-4"
          data-testid="coach-detail-pending-requests-banner"
        >
          <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 border border-[#E5D2C2]">
            <BellRing strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg sm:text-xl text-[#1A1D1A] tracking-tight font-semibold">
              {clientsWantingBusiness.length === 1
                ? "1 client wants to do business"
                : `${clientsWantingBusiness.length} clients want to do business`}
              {totalRequests > clientsWantingBusiness.length &&
                ` · ${totalRequests} open requests in total`}
            </h3>
            <p className="text-sm text-[#5C635A] mt-1 leading-relaxed">
              {clientsWantingBusiness
                .slice(0, 5)
                .map((u) => u.name)
                .join(", ")}
              {clientsWantingBusiness.length > 5
                ? `, and ${clientsWantingBusiness.length - 5} more`
                : ""}
              . Look for the badges in the list below — click "Mark handled" once {coach.name} has reached out.
            </p>
          </div>
        </div>
      )}

      <AddPersonDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        endpoint={`/coaches/${id}/users`}
        kind="user"
        onCreated={load}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent
          className="bg-white border-[#E5E2DC] sm:max-w-lg"
          data-testid="coach-detail-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold">
              Edit Client
            </DialogTitle>
            <DialogDescription className="text-[#5C635A]">
              Update the client's details. Leave password blank to keep it unchanged.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4 mt-2">
            <Field
              label="Full Name"
              testid="coach-detail-edit-name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <Field
              label="Email"
              type="email"
              testid="coach-detail-edit-email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              required
            />
            <Field
              label="New Password (optional)"
              type="password"
              testid="coach-detail-edit-password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              placeholder="Leave blank to keep current"
            />
            <BrainTypeField
              value={form.brain_type}
              onChange={(v) => setForm({ ...form, brain_type: v })}
              testid="coach-detail-edit-brain-type"
              label="Brain Type (1–9, optional)"
            />
            <ClientTypeField
              value={form.client_type}
              onChange={(v) => setForm({ ...form, client_type: v })}
              testid="coach-detail-edit-client-type"
            />
            {editError && (
              <p
                className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                data-testid="coach-detail-edit-error"
              >
                {editError}
              </p>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                data-testid="coach-detail-edit-submit"
                className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
        title="Delete this client?"
        description={
          confirmTarget
            ? `This will permanently delete "${confirmTarget.name}" and their onboarding responses. This action cannot be undone.`
            : ""
        }
        onConfirm={handleDelete}
        loading={deleting}
        testIdPrefix="coach-detail-user-delete"
      />

      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="coach-detail-users">
        <div className="grid grid-cols-12 px-6 py-4 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold border-b border-[#E5E2DC]">
          <div className="col-span-3">Client</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1 text-center">Brain</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Onboarding</div>
          <div className="col-span-1 text-right">{canManage ? "Added" : "Added"}</div>
        </div>
        {users.length === 0 ? (
          <div className="px-6 py-16 text-center text-[#5C635A]">No clients yet.</div>
        ) : (
          users.map((u) => {
            const bg = rowColorForUser(u);
            return (
              <div
                key={u.id}
                onClick={() => navigate(`/details/user/${u.id}`)}
                data-testid={`coach-detail-user-row-${u.id}`}
                style={bg ? { backgroundColor: bg } : undefined}
                className="grid grid-cols-12 px-6 py-5 border-b last:border-0 border-[#E5E2DC] items-center hover:brightness-95 transition-[filter] cursor-pointer group"
              >
                <div className="col-span-3 font-medium text-[#1A1D1A] flex flex-col gap-1.5 min-w-0">
                  <span className="flex items-center gap-2">
                    {u.name}
                    <ChevronRight
                      strokeWidth={1.5}
                      size={14}
                      className="text-[#B37A5F] opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </span>
                  {u.pending_requests && u.pending_requests.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <ClientRequestBadges
                        pendingRequests={u.pending_requests}
                        size="sm"
                        testid={`coach-detail-user-request-badges-${u.id}`}
                      />
                      <button
                        type="button"
                        onClick={(e) => markHandled(e, u.id)}
                        data-testid={`coach-detail-user-mark-handled-${u.id}`}
                        className="text-[10px] text-[#8A9188] hover:text-[#1A1D1A] font-bold uppercase tracking-[0.15em] self-start hover:underline"
                      >
                        Mark handled
                      </button>
                    </div>
                  )}
                </div>
                <div className="col-span-3 text-sm text-[#5C635A] truncate">{u.email}</div>
                <div
                  className="col-span-1 text-center font-heading text-lg text-[#1A1D1A] font-semibold"
                  data-testid={`coach-detail-user-brain-${u.id}`}
                >
                  {u.brain_type ?? "—"}
                </div>
                <div className="col-span-2">
                  <span
                    data-testid={`coach-detail-user-client-type-${u.id}`}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] bg-[#F2F0EA] text-[#1A1D1A] border border-[#E5E2DC]"
                  >
                    {CLIENT_TYPE_LABEL[u.client_type || "prospect"]}
                  </span>
                </div>
                <div className="col-span-2">
                  {u.onboarding_completed ? (
                    <span className="inline-flex items-center gap-1.5 text-[#4A5D4E] text-xs font-medium">
                      <CheckCircle2 strokeWidth={1.5} size={14} /> Complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[#8A9188] text-xs">
                      <Circle strokeWidth={1.5} size={14} /> Pending
                    </span>
                  )}
                </div>
                <div className="col-span-1 text-right text-xs text-[#8A9188] uppercase tracking-wider flex items-center justify-end gap-2">
                  <span>{new Date(u.created_at).toLocaleDateString()}</span>
                  {canManage && (
                    <RowActions
                      onEdit={() => openEdit(u)}
                      onDelete={() => setConfirmTarget(u)}
                      testIdPrefix={`coach-detail-user-${u.id}`}
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <Icon strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#8A9188] font-bold">{label}</span>
      </div>
      <p className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold truncate">{value}</p>
      {hint && <p className="text-xs text-[#8A9188] mt-2 truncate">{hint}</p>}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">{label}</Label>
      <Input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-11 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
      />
    </div>
  );
}
